/**
 * UseCaseSuitability — "Optimal Use Cases" section.
 *
 * Compact table that helps a buyer self-qualify whether the site
 * fits their actual workload. Each row is a use-case archetype with
 * a status dot and one-line reason derived from existing enrichment.
 *
 * Rubrics are deliberately conservative — only render a row when we
 * have enough data to be confident in a verdict.
 *
 * Audit 2026-05-27 (professional redesign): emoji verdicts replaced
 * with status dots; "Best fit for" header renamed to "Optimal Use
 * Cases"; "Heuristic match" footnote removed.
 */

import React from 'react';

function dcFits(site) {
  const out = [];
  const spotOre  = site.electricity_price_nok_mwh != null ? site.electricity_price_nok_mwh / 10 : null;
  const days     = site.free_cooling_days_yr;
  const ixpKm    = site.nearest_ixp_distance_km;
  const urbanKm  = site.urban_distance_km;
  const gridDist = site.nearest_grid_distance_km;
  const capacity = site.max_practical_mw;

  // 1. Compute-heavy training / batch — cheap power + free cooling matter,
  // latency does not. Strong fit if spot ≤ 50 øre and ≥ 150 free-cooling
  // days. Weak fit if either is missing or weak.
  if (spotOre != null && days != null) {
    const fit = spotOre <= 50 && days >= 150;
    out.push({
      label: 'Compute-heavy training / batch',
      dotClass: fit ? 'status-strong' : 'status-unknown',
      verdictLabel: fit ? 'Recommended' : 'Marginal',
      reason: fit
        ? `Low ${Math.round(spotOre)} øre/kWh power, ${days} free-cool days/yr`
        : (spotOre > 50 ? `Power at ${Math.round(spotOre)} øre/kWh eats into margin`
                        : `Only ${days} free-cool days/yr`),
    });
  }

  // 2. Latency-sensitive edge / cloud — needs proximity to users (cities)
  // AND short fibre path. Reject if either far. Strong fit needs urbanKm
  // ≤ 25 AND ixpKm ≤ 30.
  if (urbanKm != null && ixpKm != null) {
    const fit = urbanKm <= 25 && ixpKm <= 30;
    const blocker = urbanKm > 50 || ixpKm > 100;
    out.push({
      label: 'Latency-sensitive edge / cloud',
      dotClass: fit ? 'status-strong' : blocker ? 'status-blocker' : 'status-unknown',
      verdictLabel: fit ? 'Recommended' : blocker ? 'Not suitable' : 'Marginal',
      reason: fit
        ? `${Math.round(urbanKm)} km to settlement, ${Math.round(ixpKm)} km to IXP`
        : blocker
          ? (urbanKm > ixpKm ? `${Math.round(urbanKm)} km from nearest settlement`
                              : `${Math.round(ixpKm)} km from fibre meet point`)
          : `${Math.round(urbanKm)} km / ${Math.round(ixpKm)} km — borderline`,
    });
  }

  // 3. Crypto / GPU training — cheapest possible power, cold climate, no
  // latency requirement. Loves what edge hates.
  if (spotOre != null && days != null) {
    const fit = spotOre <= 30 && days >= 200;
    out.push({
      label: 'Crypto / GPU training',
      dotClass: fit ? 'status-strong' : 'status-unknown',
      verdictLabel: fit ? 'Recommended' : 'Marginal',
      reason: fit
        ? `Very cheap ${Math.round(spotOre)} øre/kWh power + ${days} free-cool days`
        : spotOre > 50 ? `Power too high (${Math.round(spotOre)} øre/kWh) for margin-thin GPU workloads`
                       : 'Power competitive but not bargain-tier',
    });
  }

  // Bulk storage / archive removed 2026-05-27 per audit feedback
  // ("REMOVE entirely from main view"). The capacity headroom claim
  // it relied on was also flagged as unreliable until our capacity
  // model has more validation. Re-add when the calc is verified.

  // 5. Sovereign / regulated workloads — wants in-country, near city for
  // staffing, but not so close it's costly.
  if (urbanKm != null && gridDist != null) {
    const fit = urbanKm >= 5 && urbanKm <= 60 && gridDist <= 10;
    out.push({
      label: 'Sovereign / regulated cloud',
      dotClass: fit ? 'status-strong' : 'status-unknown',
      verdictLabel: fit ? 'Recommended' : 'Marginal',
      reason: fit
        ? `${Math.round(urbanKm)} km from city (staffing) · grid ${gridDist.toFixed(1)} km`
        : urbanKm > 60 ? `${Math.round(urbanKm)} km from city — staffing burden`
                       : urbanKm < 5 ? 'Inside urban core — land cost likely high'
                       : `Grid ${gridDist.toFixed(1)} km away — connection cost`,
    });
  }

  return out;
}

function hydrogenFits(site) {
  const out = [];
  const co2     = site.nearest_co2_source;
  const portKm  = site.nearest_port_distance_km ?? site.port_distance_km;
  const roadKm  = site.nearest_road_distance_km;
  const waterKm = site.water_distance_km ?? site.nearest_water_distance_km;
  const elec    = site.electricity_price_nok_mwh;

  // 1. Methanol — needs nearby CO₂ source. Strong fit if a known CO₂
  // emitter within 25 km.
  if (co2 && co2.distance_km != null) {
    const fit = co2.distance_km <= 25;
    out.push({
      label: 'Methanol synthesis',
      dotClass: fit ? 'status-strong' : 'status-unknown',
      verdictLabel: fit ? 'Recommended' : 'Marginal',
      reason: fit
        ? `${co2.name} ${co2.distance_km} km away (CO₂ feedstock)`
        : `Nearest CO₂ source ${co2.distance_km} km — pipeline / transport overhead`,
    });
  }

  // 2. Ammonia / maritime export — needs deep-water port nearby.
  if (portKm != null) {
    const fit = portKm <= 15;
    out.push({
      label: 'Ammonia / maritime export',
      dotClass: fit ? 'status-strong' : portKm > 50 ? 'status-blocker' : 'status-unknown',
      verdictLabel: fit ? 'Recommended' : portKm > 50 ? 'Not suitable' : 'Marginal',
      reason: fit ? `Port ${portKm.toFixed(1)} km — short trucking / pipeline`
                  : portKm > 50 ? `Port ${portKm.toFixed(0)} km — long inland haul`
                                : `Port ${portKm.toFixed(1)} km — borderline`,
    });
  }

  // 3. Mobility / HRS (hydrogen refuelling stations) — needs main road
  // network access.
  if (roadKm != null) {
    const fit = roadKm <= 2;
    out.push({
      label: 'Mobility / HRS supply',
      dotClass: fit ? 'status-strong' : 'status-unknown',
      verdictLabel: fit ? 'Recommended' : 'Marginal',
      reason: fit ? `Road ${roadKm.toFixed(1)} km — easy distribution`
                  : `${roadKm.toFixed(1)} km off-road — distribution overhead`,
    });
  }

  // 4. Industrial off-take (steel, fertilizer) — needs cheap power AND
  // water for cooling/process.
  if (elec != null && waterKm != null) {
    const fit = elec <= 600 && waterKm <= 2;
    const ore = Math.round(elec / 10);
    out.push({
      label: 'Industrial off-take (steel / fertilizer)',
      dotClass: fit ? 'status-strong' : 'status-unknown',
      verdictLabel: fit ? 'Recommended' : 'Marginal',
      reason: fit ? `${ore} øre/kWh power, water ${waterKm.toFixed(1)} km`
                  : ore > 60 ? `Power ${ore} øre/kWh too high for industrial off-take` : `Water ${waterKm.toFixed(1)} km away`,
    });
  }

  return out;
}

export default function UseCaseSuitability({ site, useCase }) {
  if (!site) return null;
  const rows = useCase === 'data_center' ? dcFits(site) : hydrogenFits(site);
  if (rows.length === 0) return null;

  return (
    <div className="analysis-section">
      <div className="analysis-section-header">Optimal Use Cases</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map(r => (
          <div key={r.label} style={{ display: 'flex', alignItems: 'baseline', gap: 10, fontSize: 12 }}>
            <span style={{ flex: '0 0 16px', display: 'flex', alignItems: 'center' }}>
              <span className={`status-dot ${r.dotClass}`} />
            </span>
            <span style={{ flex: '0 0 240px' }}>{r.label}</span>
            <span style={{ flex: 1, opacity: 0.75 }}>{r.reason}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
